import { useState } from 'react';

const HEIGHT = 200;
const PLOT_TOP = 16;
const PLOT_BOTTOM = 28;
const AXIS_WIDTH = 40;
const BAR_GAP = 12;

export function CplChart({
  platform,
  dailyCpl,
  target = null,
  labels,
}: {
  platform: string;
  dailyCpl: number[];
  target?: number | null;
  labels?: string[];
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  const labelEvery = Math.ceil(dailyCpl.length / 10);
  const width = 720;
  const maxValue = Math.max(10, Math.ceil((Math.max(target ?? 0, ...dailyCpl) * 1.15) / 10) * 10);
  const plotHeight = HEIGHT - PLOT_TOP - PLOT_BOTTOM;
  const plotWidth = width - AXIS_WIDTH;
  const slot = plotWidth / dailyCpl.length;
  const barWidth = Math.min(48, slot - BAR_GAP);
  const y = (v: number) => PLOT_TOP + plotHeight - (v / maxValue) * plotHeight;
  const targetY = target === null ? null : y(target);

  return (
    <figure className="flex flex-col gap-2">
      <svg
        viewBox={`0 0 ${width} ${HEIGHT}`}
        className="w-full"
        role="img"
        aria-label={`${platform} daily cost per result over ${dailyCpl.length} days${target === null ? '' : ` against a $${target} target`}`}
      >
        <line
          x1={AXIS_WIDTH}
          x2={width}
          y1={PLOT_TOP + plotHeight}
          y2={PLOT_TOP + plotHeight}
          stroke="var(--color-border-primary)"
        />
        {dailyCpl.map((value, index) => ({ day: index + 1, value })).map(({ day, value }) => {
          const i = day - 1;
          const x = AXIS_WIDTH + i * slot + (slot - barWidth) / 2;
          const top = y(value);
          const over = target !== null && value > target;
          return (
            <g
              key={day}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              <rect x={AXIS_WIDTH + i * slot} y={PLOT_TOP} width={slot} height={plotHeight} fill="transparent" />
              <path
                d={`M${x},${PLOT_TOP + plotHeight} V${top + 4} q0,-4 4,-4 h${barWidth - 8} q4,0 4,4 V${PLOT_TOP + plotHeight} Z`}
                fill={over ? 'var(--color-bg-orange)' : 'var(--color-bg-tertiary)'}
                opacity={hovered === null || hovered === i ? 1 : 0.55}
              />
              {(i % labelEvery === 0 || i === dailyCpl.length - 1) && (
                <text
                  x={AXIS_WIDTH + i * slot + slot / 2}
                  y={HEIGHT - 8}
                  textAnchor="middle"
                  className="fill-[var(--color-content-tertiary)] text-[11px]"
                >
                  {labels?.[i] ?? `Day ${i + 1}`}
                </text>
              )}
              {hovered === i && (
                <text
                  x={AXIS_WIDTH + i * slot + slot / 2}
                  y={top - 6}
                  textAnchor="middle"
                  className="fill-[var(--color-content-primary)] font-mono text-[12px] font-semibold"
                >
                  ${Number.isInteger(value) ? value : value.toFixed(2)}
                </text>
              )}
            </g>
          );
        })}
        {targetY !== null && (
          <>
            <line
              x1={AXIS_WIDTH}
              x2={width}
              y1={targetY}
              y2={targetY}
              stroke="var(--color-content-secondary)"
              strokeWidth={1.5}
              strokeDasharray="5 4"
              pointerEvents="none"
            />
            <text x={0} y={targetY + 4} className="fill-[var(--color-content-secondary)] font-mono text-[11px]">
              ${target}
            </text>
          </>
        )}
      </svg>
      {target !== null && (
        <figcaption className="flex items-center gap-4 text-xs text-content-tertiary">
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-sm bg-bg-tertiary" />
            At or under target
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-sm bg-bg-orange" />
            Above target
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-4 border-t-[1.5px] border-dashed border-content-secondary" />
            ${target} target
          </span>
        </figcaption>
      )}
    </figure>
  );
}
